import { sanitizeName } from './nameUtils';

/** Mirrors the server's own ignore list so we never read (or send) gigabytes
 * of node_modules that would be rebuilt there anyway. */
const IGNORED_DIRS = new Set([
  'node_modules', '.git', '.next', '.nuxt', 'dist', 'build', '.venv', 'venv', '__pycache__',
  '.cache', '.turbo', '.idea', '.vscode', '__MACOSX', 'target', '.gradle',
]);
const MAX_BYTES = 40 * 1024 * 1024;

export interface PickedFile {
  /** Path relative to the dropped folder's parent, e.g. "my-app/src/a.js". */
  path: string;
  file: File;
}

export type PickedSource =
  | { kind: 'zip'; file: File; label: string }
  | { kind: 'files'; files: PickedFile[]; label: string };

function ignored(path: string): boolean {
  const parts = path.split('/');
  return parts.slice(0, -1).some((d) => IGNORED_DIRS.has(d)) || parts[parts.length - 1] === '.DS_Store';
}

// Entry/Reader types for the non-standard but universally supported
// webkitGetAsEntry API; the DOM lib typings are partial.
interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
  file?: (cb: (f: File) => void, err: (e: unknown) => void) => void;
  createReader?: () => { readEntries: (cb: (e: FsEntry[]) => void, err: (e: unknown) => void) => void };
}

async function walk(entry: FsEntry, out: PickedFile[]): Promise<void> {
  const path = entry.fullPath.replace(/^\//, '');
  if (entry.isFile && entry.file) {
    if (ignored(path)) return;
    const file = await new Promise<File>((res, rej) => entry.file!(res, rej));
    out.push({ path, file });
  } else if (entry.isDirectory && entry.createReader) {
    if (IGNORED_DIRS.has(entry.name)) return;
    const reader = entry.createReader();
    // readEntries returns results in batches; loop until empty.
    for (;;) {
      const batch = await new Promise<FsEntry[]>((res, rej) => reader.readEntries(res, rej));
      if (batch.length === 0) break;
      for (const child of batch) await walk(child, out);
    }
  }
}

function finish(files: PickedFile[], label: string): PickedSource {
  const total = files.reduce((n, f) => n + f.file.size, 0);
  if (files.length === 0) throw new Error('That folder is empty.');
  if (total > MAX_BYTES) throw new Error('That project is larger than 40 MB. Remove build output and large assets, then try again.');
  return { kind: 'files', files, label };
}

/** Handles a drop of a ZIP, a folder, or loose files. */
export async function pickFromDrop(dt: DataTransfer): Promise<PickedSource> {
  const items = Array.from(dt.items ?? []);
  const entries: FsEntry[] = [];
  for (const i of items) {
    const e = (i as unknown as { webkitGetAsEntry?: () => FsEntry | null }).webkitGetAsEntry?.();
    if (e) entries.push(e);
  }

  if (entries.length === 1 && entries[0].isFile && /\.zip$/i.test(entries[0].name)) {
    return pickZip(dt.files[0]);
  }
  const files: PickedFile[] = [];
  for (const e of entries) await walk(e, files);
  const label = entries.length === 1 && entries[0].isDirectory ? entries[0].name : 'your project';
  return finish(files, label);
}

export function pickZip(file: File): PickedSource {
  if (file.size > MAX_BYTES) throw new Error('That ZIP is larger than 40 MB.');
  return { kind: 'zip', file, label: file.name.replace(/\.zip$/i, '') };
}

/** Handles <input type=file webkitdirectory> and plain multi-select. */
export function pickFromInput(list: FileList): PickedSource {
  const all = Array.from(list);
  if (all.length === 1 && /\.zip$/i.test(all[0].name)) return pickZip(all[0]);
  const files = all
    .map((file) => ({ path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name, file }))
    .filter((f) => !ignored(f.path));
  const first = files[0]?.path.split('/')[0];
  return finish(files, files.some((f) => f.path.includes('/')) && first ? first : 'your project');
}

/** Folder/zip name -> a valid app name (lowercase, starts with a letter). */
export function suggestAppName(label: string): string {
  let n = sanitizeName(label).replace(/-+/g, '-').slice(0, 40).replace(/-+$/, '');
  if (!/^[a-z]/.test(n)) n = n ? `app-${n}` : 'my-app';
  return n;
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export async function toRequestSource(src: PickedSource) {
  if (src.kind === 'zip') return { type: 'zip' as const, zipBase64: await fileToBase64(src.file) };
  const files: { path: string; contentBase64: string }[] = [];
  for (const f of src.files) files.push({ path: f.path, contentBase64: await fileToBase64(f.file) });
  return { type: 'files' as const, files };
}
